/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/lookup_transaction_types-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-lookup_transaction_types-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/lookup_transaction_types-entity-config.ts
 * - Run: pnpm run generate:api-clients (regenerates this file)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id
 * - Sortable fields: created_at, updated_at
 * - Required fields: id, archived, sort_order, updated_at
 * - Readonly fields: id, created_at, updated_at
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/lookup_transaction_types-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-lookup_transaction_types-api-client.ts (API client)
 * - client/api/generated_schemas/lookup_transaction_types.ts (TypeBox schemas and types)
 *
 * Source config: config/entities/lookup_transaction_types-entity-config.ts
 * Public fields: 8/8 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LookupTransactionTypesClient, LookupTransactionTypesQueryOptions } from '../generated_clients/generated-lookup_transaction_types-api-client.js';
import {
  LookupTransactionTypesInsert,
  LookupTransactionTypesUpdate,
  LookupTransactionTypesFieldConfig
} from '../../types/generated_schemas/generated-lookup_transaction_types-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/lookup_transaction_types_business_hooks/

// Query keys with enhanced caching strategy. A list key carries every
// parameter getAll sends, so two different pages never share a cache entry.
export const lookupTransactionTypesKeys = {
  all: ['lookup_transaction_types'] as const,
  lists: () => [...lookupTransactionTypesKeys.all, 'list'] as const,
  list: (filters: Record<string, any>, page?: Pick<LookupTransactionTypesQueryOptions, 'limit' | 'offset' | 'sortBy' | 'sortOrder'>) =>
    [...lookupTransactionTypesKeys.lists(), { filters, ...page }] as const,
  details: () => [...lookupTransactionTypesKeys.all, 'detail'] as const,
  detail: (id: string) => [...lookupTransactionTypesKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...lookupTransactionTypesKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...lookupTransactionTypesKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...lookupTransactionTypesKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...lookupTransactionTypesKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useLookupTransactionTypesClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new LookupTransactionTypesClient();
};

/**
 * Hook to get all lookup_transaction_types with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with lookup_transaction_types data
 */
export const useLookupTransactionTypesList = (options?: LookupTransactionTypesQueryOptions) => {
  const client = useLookupTransactionTypesClient();
  
  return useQuery({
    queryKey: lookupTransactionTypesKeys.list(options?.filters || {}, {
      limit: options?.limit,
      offset: options?.offset,
      sortBy: options?.sortBy,
      sortOrder: options?.sortOrder,
    }),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single lookup_transaction_type by ID
 *
 * @param id - The lookup_transaction_type ID
 * @returns React Query result with single lookup_transaction_type data
 */
export const useLookupTransactionTypes = (id: string) => {
  const client = useLookupTransactionTypesClient();
  
  return useQuery({
    queryKey: lookupTransactionTypesKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/lookup_transaction_types
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/lookup_transaction_types_business_hooks/
 */

/**
 * Hook to create a new lookup_transaction_type
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id, archived, sort_order, updated_at
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at
 *
 * @returns React Query mutation for creating lookup_transaction_type
 */
export const useCreateLookupTransactionTypes = () => {
  const client = useLookupTransactionTypesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: LookupTransactionTypesInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lookupTransactionTypesKeys.all });
    },
  });
};

/**
 * Hook to update an existing lookup_transaction_type
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at
 *
 * @returns React Query mutation for updating lookup_transaction_type
 */
export const useUpdateLookupTransactionTypes = () => {
  const client = useLookupTransactionTypesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: LookupTransactionTypesUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: lookupTransactionTypesKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: lookupTransactionTypesKeys.lists() });
    },
  });
};

/**
 * Hook to delete a lookup_transaction_type
 *
 * @returns React Query mutation for deleting lookup_transaction_type
 */
export const useDeleteLookupTransactionTypes = () => {
  const client = useLookupTransactionTypesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lookupTransactionTypesKeys.all });
    },
  });
};

/**
 * Hook to count lookup_transaction_types with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useLookupTransactionTypesCount = (filter?: Partial<Record<typeof LookupTransactionTypesFieldConfig.filterable[number], any>>) => {
  const client = useLookupTransactionTypesClient();
  
  return useQuery({
    queryKey: lookupTransactionTypesKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useLookupTransactionTypesFieldConfig = () => {
  return LookupTransactionTypesFieldConfig;
};